import { onScopeDispose, readonly, ref, type Ref } from 'vue'

/** An append-only text buffer that reveals what it has received at a readable rate. */
export interface StreamedText {
  /** The prefix of the received text that should be rendered right now. */
  revealed: Readonly<Ref<string>>
  /** Appends newly received text to the buffer. */
  push: (text: string) => void
  /** Resolves once everything received has been revealed, or once `reset` runs. */
  drain: () => Promise<void>
  /** Drops the buffer and the revealed text immediately. */
  reset: () => void
}

const FALLBACK_RATE = 260
const FALLBACK_BACKLOG_SCALE = 240

function readRate(name: string, fallback: number): number {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return fallback
  const value = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(name).trim(),
  )
  return Number.isFinite(value) && value > 0 ? value : fallback
}

const REVEAL_RATE = readRate('--stream-reveal-rate', FALLBACK_RATE)
const BACKLOG_SCALE = readRate('--stream-reveal-backlog-scale', FALLBACK_BACKLOG_SCALE)

function revealsInstantly(): boolean {
  if (typeof requestAnimationFrame !== 'function') return true
  if (typeof matchMedia !== 'function') return false
  return matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function useStreamedText(): StreamedText {
  const revealed = ref('')

  let received = ''
  let shown = 0
  let frame: number | null = null
  let lastAt: number | null = null
  let waiters: Array<() => void> = []

  function settleWaiters(): void {
    const pending = waiters
    waiters = []
    for (const resolve of pending) resolve()
  }

  function cancelFrame(): void {
    if (frame === null) return
    cancelAnimationFrame(frame)
    frame = null
  }

  function wordBoundary(count: number): number {
    if (count >= received.length) return received.length
    const head = received.slice(0, count)
    const space = head.lastIndexOf(' ')
    const newline = head.lastIndexOf('\n')
    const cut = Math.max(space, newline)
    return cut < 0 ? count : cut + 1
  }

  function paint(): void {
    revealed.value = received.slice(0, wordBoundary(Math.floor(shown)))
  }

  function tick(now: number): void {
    frame = null
    const elapsed = lastAt === null ? 0 : Math.max(0, (now - lastAt) / 1000)
    lastAt = now

    const backlog = received.length - shown
    const rate = REVEAL_RATE * (1 + backlog / BACKLOG_SCALE)
    shown = Math.min(received.length, shown + rate * elapsed)
    paint()

    if (shown >= received.length) {
      lastAt = null
      settleWaiters()
      return
    }
    schedule()
  }

  function schedule(): void {
    if (frame !== null) return
    frame = requestAnimationFrame(tick)
  }

  function push(text: string): void {
    if (!text) return
    received += text

    if (revealsInstantly()) {
      shown = received.length
      revealed.value = received
      settleWaiters()
      return
    }
    schedule()
  }

  function drain(): Promise<void> {
    if (shown >= received.length) return Promise.resolve()
    return new Promise<void>((resolve) => {
      waiters.push(resolve)
    })
  }

  function reset(): void {
    cancelFrame()
    lastAt = null
    received = ''
    shown = 0
    revealed.value = ''
    settleWaiters()
  }

  onScopeDispose(() => {
    cancelFrame()
    settleWaiters()
  })

  return { revealed: readonly(revealed), push, drain, reset }
}
